import React, { useState } from 'react';
import Layout from '../../components/Layout';

export default function DesignSystemPage() {
  const [activeTab, setActiveTab] = useState('card');

  const CodeBlock = ({ children }: { children: string }) => (
    <pre className="bop-card bop-design-system-page__code-block">
      <code>{children}</code>
    </pre>
  );

  return (
    <Layout>
      <div className="bop-page-header">
        <h1 className="bop-page-title">BOP Design System</h1>
      </div>

      <div className="bop-card bop-design-system-page__intro">
        <p className="bop-text-muted bop-mb-0">
          Sistema di design CSS riutilizzabile per Backoffice Pianinvest. Tutti i componenti visivi
          utilizzano classi CSS definite in <code>bop-design-system.css</code> invece di stili inline.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bop-tabs">
        <button className={`bop-tab ${activeTab === 'card' ? 'active' : ''}`} onClick={() => setActiveTab('card')}>
          Card
        </button>
        <button className={`bop-tab ${activeTab === 'buttons' ? 'active' : ''}`} onClick={() => setActiveTab('buttons')}>
          Bottoni
        </button>
        <button className={`bop-tab ${activeTab === 'form' ? 'active' : ''}`} onClick={() => setActiveTab('form')}>
          Form
        </button>
        <button className={`bop-tab ${activeTab === 'table' ? 'active' : ''}`} onClick={() => setActiveTab('table')}>
          Tabelle
        </button>
        <button className={`bop-tab ${activeTab === 'badges' ? 'active' : ''}`} onClick={() => setActiveTab('badges')}>
          Badge
        </button>
        <button className={`bop-tab ${activeTab === 'layout' ? 'active' : ''}`} onClick={() => setActiveTab('layout')}>
          Layout
        </button>
        <button className={`bop-tab ${activeTab === 'utility' ? 'active' : ''}`} onClick={() => setActiveTab('utility')}>
          Utility
        </button>
      </div>

      {/* Card Section */}
      {activeTab === 'card' && (
        <>
          <h2 className="bop-design-system-page__section-title">Card</h2>

          <div className="bop-card">
            <h3 className="bop-design-system-page__example-title">Esempio Card</h3>
            <p className="bop-text-muted bop-mb-0">
              Questo è un contenitore card standard con padding 24px, border-radius 12px e box-shadow.
            </p>
          </div>

          <CodeBlock>{`<div className="bop-card">
  <h3>Titolo Card</h3>
  <p>Contenuto della card...</p>
</div>`}</CodeBlock>
        </>
      )}

      {/* Buttons Section */}
      {activeTab === 'buttons' && (
        <>
          <h2 className="bop-design-system-page__section-title">Bottoni</h2>

          <div className="bop-card">
            <div className="bop-flex bop-design-system-page__flex-wrap-gap">
              <button className="bop-btn-primary">Primary Button</button>
              <button className="bop-btn-secondary">Secondary Button</button>
              <button className="bop-btn-danger">Danger Button</button>
              <button className="bop-btn-icon">🔍</button>
            </div>
          </div>

          <CodeBlock>{`<button className="bop-btn-primary">Primary Button</button>
<button className="bop-btn-secondary">Secondary Button</button>
<button className="bop-btn-danger">Danger Button</button>
<button className="bop-btn-icon">🔍</button>`}</CodeBlock>
        </>
      )}

      {/* Form Section */}
      {activeTab === 'form' && (
        <>
          <h2 className="bop-design-system-page__section-title">Form</h2>

          <div className="bop-card">
            <div className="bop-form-group">
              <label className="bop-label">Nome</label>
              <input type="text" className="bop-input" placeholder="Inserisci il nome" />
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Categoria</label>
              <select className="bop-select">
                <option>Seleziona...</option>
                <option>Opzione 1</option>
                <option>Opzione 2</option>
              </select>
            </div>

            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label">Campo 1</label>
                <input type="text" className="bop-input" placeholder="Valore 1" />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Campo 2</label>
                <input type="text" className="bop-input" placeholder="Valore 2" />
              </div>
            </div>
          </div>

          <CodeBlock>{`<div className="bop-form-group">
  <label className="bop-label">Nome</label>
  <input type="text" className="bop-input" />
</div>

<div className="bop-form-group">
  <label className="bop-label">Categoria</label>
  <select className="bop-select">
    <option>Seleziona...</option>
  </select>
</div>

<div className="bop-form-row">
  <div className="bop-form-group">...</div>
  <div className="bop-form-group">...</div>
</div>`}</CodeBlock>
        </>
      )}

      {/* Table Section */}
      {activeTab === 'table' && (
        <>
          <h2 className="bop-design-system-page__section-title">Tabelle</h2>

          <div className="bop-card">
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Ruolo</th>
                  <th>Stato</th>
                  <th className="bop-text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Mario Rossi</td>
                  <td>Admin</td>
                  <td><span className="bop-badge bop-badge-success">Attivo</span></td>
                  <td className="bop-text-right">
                    <button className="bop-btn-icon">✏️</button>
                  </td>
                </tr>
                <tr>
                  <td>Laura Bianchi</td>
                  <td>User</td>
                  <td><span className="bop-badge bop-badge-neutral">Inattivo</span></td>
                  <td className="bop-text-right">
                    <button className="bop-btn-icon">✏️</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <CodeBlock>{`<table className="bop-table">
  <thead>
    <tr>
      <th>Nome</th>
      <th>Ruolo</th>
      <th className="bop-text-right">Azioni</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Mario Rossi</td>
      <td>Admin</td>
      <td className="bop-text-right">...</td>
    </tr>
  </tbody>
</table>`}</CodeBlock>
        </>
      )}

      {/* Badges Section */}
      {activeTab === 'badges' && (
        <>
          <h2 className="bop-design-system-page__section-title">Badge</h2>

          <div className="bop-card">
            <div className="bop-flex bop-design-system-page__flex-wrap-gap">
              <span className="bop-badge bop-badge-success">Success</span>
              <span className="bop-badge bop-badge-danger">Danger</span>
              <span className="bop-badge bop-badge-warning">Warning</span>
              <span className="bop-badge bop-badge-info">Info</span>
              <span className="bop-badge bop-badge-neutral">Neutral</span>
            </div>
          </div>

          <CodeBlock>{`<span className="bop-badge bop-badge-success">Success</span>
<span className="bop-badge bop-badge-danger">Danger</span>
<span className="bop-badge bop-badge-warning">Warning</span>
<span className="bop-badge bop-badge-info">Info</span>
<span className="bop-badge bop-badge-neutral">Neutral</span>`}</CodeBlock>
        </>
      )}

      {/* Layout Section */}
      {activeTab === 'layout' && (
        <>
          <h2 className="bop-design-system-page__section-title">Layout</h2>

          <div className="bop-card">
            <h3 className="bop-design-system-page__sub-title">Page Header</h3>
            <div className="bop-page-header bop-mb-0">
              <h1 className="bop-page-title">Titolo Pagina</h1>
              <button className="bop-btn-primary">Azione</button>
            </div>
          </div>

          <CodeBlock>{`<div className="bop-page-header">
  <h1 className="bop-page-title">Titolo Pagina</h1>
  <button className="bop-btn-primary">Azione</button>
</div>`}</CodeBlock>

          <div className="bop-card">
            <h3 className="bop-design-system-page__sub-title">Filter Bar</h3>
            <div className="bop-filter-bar bop-mb-0">
              <input type="text" className="bop-input bop-design-system-page__filter-input" placeholder="Cerca..." />
              <select className="bop-select bop-design-system-page__filter-select">
                <option>Tutti</option>
                <option>Attivi</option>
              </select>
              <button className="bop-btn-secondary">Filtra</button>
            </div>
          </div>

          <CodeBlock>{`<div className="bop-filter-bar">
  <input type="text" className="bop-input" placeholder="Cerca..." />
  <select className="bop-select">...</select>
  <button className="bop-btn-secondary">Filtra</button>
</div>`}</CodeBlock>

          <div className="bop-card">
            <h3 className="bop-design-system-page__sub-title">Empty State</h3>
            <div className="bop-empty-state">
              Nessun elemento trovato
            </div>
          </div>

          <CodeBlock>{`<div className="bop-empty-state">
  Nessun elemento trovato
</div>`}</CodeBlock>
        </>
      )}

      {/* Utility Section */}
      {activeTab === 'utility' && (
        <>
          <h2 className="bop-design-system-page__section-title">Utility Classes</h2>

          <div className="bop-card">
            <h3 className="bop-design-system-page__sub-title">Text Alignment</h3>
            <div className="bop-design-system-page__demo-row">
              <div className="bop-text-right">Testo allineato a destra</div>
            </div>
            <div className="bop-design-system-page__demo-row">
              <div className="bop-text-center">Testo centrato</div>
            </div>
          </div>

          <CodeBlock>{`<div className="bop-text-right">Testo a destra</div>
<div className="bop-text-center">Testo centrato</div>`}</CodeBlock>

          <div className="bop-card">
            <h3 className="bop-design-system-page__sub-title">Text Colors</h3>
            <div className="bop-design-system-page__demo-row">
              <div className="bop-text-muted">Testo muted (grigio)</div>
            </div>
            <div className="bop-design-system-page__demo-row">
              <div className="bop-text-danger">Testo danger (rosso)</div>
            </div>
          </div>

          <CodeBlock>{`<div className="bop-text-muted">Testo muted</div>
<div className="bop-text-danger">Testo danger</div>`}</CodeBlock>

          <div className="bop-card">
            <h3 className="bop-design-system-page__sub-title">Flexbox</h3>
            <div className="bop-flex bop-design-system-page__demo-row">
              <span>Elemento 1</span>
              <span>Elemento 2</span>
              <span>Elemento 3</span>
            </div>
            <div className="bop-flex-between">
              <span>Sinistra</span>
              <span>Destra</span>
            </div>
          </div>

          <CodeBlock>{`<div className="bop-flex">
  <span>Elemento 1</span>
  <span>Elemento 2</span>
</div>

<div className="bop-flex-between">
  <span>Sinistra</span>
  <span>Destra</span>
</div>`}</CodeBlock>

          <div className="bop-card">
            <h3 className="bop-design-system-page__sub-title">Spacing</h3>
            <div className="bop-mt-0">Elemento con margin-top: 0</div>
            <div className="bop-mb-0">Elemento con margin-bottom: 0</div>
          </div>

          <CodeBlock>{`<div className="bop-mt-0">Margin-top: 0</div>
<div className="bop-mb-0">Margin-bottom: 0</div>`}</CodeBlock>
        </>
      )}
    </Layout>
  );
}
